import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useChatContext } from "stream-chat-react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { ScheduleTimeModal } from "./ScheduleMessageMenu";
import { scheduledMessageErrorText, useScheduledMessages } from "./ScheduledMessagesContext";
import { formatScheduledTime, getChannelDeadline } from "./scheduledMessageUtils";
import { getChannelDisplayName } from "./chatSidebarSectionUtils";

const confirmStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  maxWidth: "calc(100vw - 32px)",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: 24,
  outline: "none",
  p: 3,
};

// Names for the channels the list points at. Channels the sidebar hasn't
// loaded are fetched once, by cid.
const useChannelNames = (cids) => {
  const { client } = useChatContext();
  const [names, setNames] = useState({});
  const key = cids.join(",");

  useEffect(() => {
    if (!client?.userID || !cids.length) return undefined;
    let cancelled = false;
    const nameOf = (channel) => getChannelDisplayName(channel, client.userID) || "Conversation";
    const known = {};
    const missing = [];
    cids.forEach((cid) => {
      const channel = client.activeChannels?.[cid];
      if (channel) known[cid] = nameOf(channel);
      else missing.push(cid);
    });
    setNames((previous) => ({ ...previous, ...known }));
    // queryChannels returns at most 30 channels a call: ask in batches.
    for (let start = 0; start < missing.length; start += 30) {
      const batch = missing.slice(start, start + 30);
      client
        .queryChannels({ cid: { $in: batch } }, {}, { limit: 30, watch: false, presence: false })
        .then((channels) => {
          if (cancelled) return;
          const fetched = {};
          channels.forEach((channel) => {
            fetched[channel.cid] = nameOf(channel);
          });
          setNames((previous) => ({ ...previous, ...fetched }));
        })
        .catch((error) => console.error("Error loading scheduled channels:", error));
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, key]);

  return names;
};

const capitalize = (text) => (text ? text[0].toUpperCase() + text.slice(1) : text);

const attachmentName = (attachment) =>
  attachment?.title || attachment?.fallback || attachment?.file_name || "Attachment";

// Channels get the sidebar's "#" tile, direct messages a round initial.
function RowAvatar({ row, name }) {
  if (row.channel_type !== "messaging") {
    return (
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-600 text-lg font-bold text-white">
        #
      </div>
    );
  }
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-700 text-sm font-bold text-white">
      {(name || "?").trim()[0]?.toUpperCase() || "?"}
    </div>
  );
}

function IconAction({ label, onClick, disabled, danger, children }) {
  return (
    <Tooltip title={label} placement="top">
      <span>
        <button
          type="button"
          aria-label={label}
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation();
            onClick();
          }}
          className={`flex h-8 w-8 items-center justify-center rounded-md hover:bg-slate-100 disabled:opacity-40 ${
            danger ? "text-red-700" : "text-slate-600"
          }`}
        >
          {children}
        </button>
      </span>
    </Tooltip>
  );
}

function ScheduledRow({ row, channelName, onOpenChannel }) {
  const scheduled = useScheduledMessages();
  const { client } = useChatContext();
  const channelDeadline = getChannelDeadline(client?.activeChannels?.[row.cid]);
  // null | "edit" (text and time) | "reschedule" (time only)
  const [editMode, setEditMode] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const failed = row.status === "failed";
  const sending = row.status === "sending";
  // Past its time but not out yet: the once-a-minute sender hasn't got to it.
  const overdue = !failed && new Date(row.send_at) <= new Date();
  const name = channelName || "Conversation";
  const files = row.attachments || [];

  const run = async (action, fallback) => {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      toast.error(scheduledMessageErrorText(error, fallback), { autoClose: 4000 });
    } finally {
      setBusy(false);
    }
  };

  const iconSx = { fontSize: 18 };

  return (
    // Slack's Scheduled list: the whole row opens the conversation; the send
    // time gives way to the actions on hover or keyboard focus. A message
    // that didn't send keeps its actions in view.
    <div
      className={`group flex cursor-pointer items-center gap-x-3.5 border-b px-8 py-3.5 ${
        failed
          ? "border-red-100 bg-red-50"
          : "border-slate-100 hover:bg-slate-50 focus-within:bg-slate-50"
      }`}
      onClick={() => onOpenChannel(row)}
    >
      <RowAvatar row={row} name={name} />
      <div className="flex min-w-0 flex-1 flex-col gap-y-0.5">
        <div className="flex min-w-0 items-center gap-x-2 text-sm">
          <span className="truncate">
            <span className="text-slate-500">To </span>
            <button
              type="button"
              className="font-semibold text-slate-900 hover:underline"
              onClick={(event) => {
                event.stopPropagation();
                onOpenChannel(row);
              }}
            >
              {name}
            </button>
          </span>
          {row.parent_id && (
            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
              Thread reply
            </span>
          )}
        </div>
        {row.text ? (
          <p className="truncate text-sm text-slate-600">{row.text}</p>
        ) : null}
        {files.length > 0 && (
          <p className="flex min-w-0 items-center gap-x-1 text-xs text-slate-500">
            <AttachFileIcon style={{ fontSize: 14 }} />
            <span className="truncate">
              {files.length === 1 ? attachmentName(files[0]) : `${files.length} files`}
            </span>
          </p>
        )}
        {failed && (
          <p className="flex items-center gap-x-1.5 text-xs text-red-700">
            <ErrorOutlineIcon style={{ fontSize: 14 }} />
            <span>Not sent. {row.failure_reason || "Something went wrong."}</span>
          </p>
        )}
      </div>

      {failed ? (
        <div className="flex shrink-0 items-center gap-x-2">
          <button
            type="button"
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              setEditMode("reschedule");
            }}
            className="h-9 rounded-lg border border-violet-200 bg-white px-3.5 text-sm font-semibold text-violet-700 hover:bg-violet-50 disabled:opacity-40"
          >
            Reschedule
          </button>
          <IconAction label="Delete" danger disabled={busy} onClick={() => setConfirmingDelete(true)}>
            <DeleteOutlineIcon sx={iconSx} />
          </IconAction>
        </div>
      ) : sending ? (
        <span className="shrink-0 text-sm text-slate-500">Sending...</span>
      ) : (
        <div className="flex shrink-0 items-center">
          <span className="flex items-center gap-x-1.5 text-sm text-slate-500 group-hover:hidden group-focus-within:hidden">
            <ScheduleIcon style={{ fontSize: 15 }} />
            {overdue ? "Sending soon" : capitalize(formatScheduledTime(row.send_at))}
          </span>
          <div className="hidden items-center gap-x-0.5 rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm group-hover:flex group-focus-within:flex">
            <IconAction
              label="Send now"
              disabled={busy}
              onClick={() => run(() => scheduled.sendNow(row.id), "We couldn't send this message.")}
            >
              <SendOutlinedIcon sx={iconSx} />
            </IconAction>
            <IconAction label="Edit message" disabled={busy} onClick={() => setEditMode("edit")}>
              <EditOutlinedIcon sx={iconSx} />
            </IconAction>
            <IconAction label="Reschedule" disabled={busy} onClick={() => setEditMode("reschedule")}>
              <ScheduleIcon sx={iconSx} />
            </IconAction>
            <IconAction label="Delete" danger disabled={busy} onClick={() => setConfirmingDelete(true)}>
              <DeleteOutlineIcon sx={iconSx} />
            </IconAction>
          </div>
        </div>
      )}

      {/* Modals portal out of the row; stop their clicks reaching it */}
      <div onClick={(event) => event.stopPropagation()}>
        <ScheduleTimeModal
          open={Boolean(editMode)}
          onClose={() => setEditMode(null)}
          title={editMode === "edit" ? "Edit scheduled message" : "Reschedule message"}
          confirmLabel="Save"
          editText={editMode === "edit"}
          initialText={row.text}
          initialSendAt={row.send_at}
          attachments={row.attachments}
          channelDeadline={channelDeadline}
          onConfirm={async ({ sendAt, text }) => {
            try {
              await scheduled.update(row.id, { sendAt, text });
            } catch (error) {
              throw new Error(scheduledMessageErrorText(error, "We couldn't save this message."));
            }
          }}
        />
        <Modal open={confirmingDelete} onClose={() => setConfirmingDelete(false)}>
          <Box sx={confirmStyle}>
            <h2 className="text-lg font-semibold text-slate-900">Delete scheduled message?</h2>
            <p className="mt-2 text-sm text-slate-600">It won't be sent, and this can't be undone.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
                onClick={() => setConfirmingDelete(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                onClick={() => {
                  setConfirmingDelete(false);
                  run(() => scheduled.cancel(row.id), "We couldn't delete this message.");
                }}
              >
                Delete
              </button>
            </div>
          </Box>
        </Modal>
      </div>
    </div>
  );
}

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

// The Scheduled view: every message the user has waiting (or that failed),
// failed first, then soonest first.
export default function ScheduledMessagesPanel({ onOpenChannel }) {
  const scheduled = useScheduledMessages();
  const rows = [...(scheduled?.scheduledMessages || [])].sort((a, b) => {
    if ((a.status === "failed") !== (b.status === "failed")) return a.status === "failed" ? -1 : 1;
    return new Date(a.send_at) - new Date(b.send_at);
  });
  const cids = Array.from(new Set(rows.map((row) => row.cid))).sort();
  const names = useChannelNames(cids);
  const failedCount = rows.filter((row) => row.status === "failed").length;
  const waitingCount = rows.length - failedCount;
  const summary = [
    waitingCount ? `${plural(waitingCount, "message")} waiting to send` : null,
    failedCount ? `${failedCount} not sent` : null,
  ]
    .filter(Boolean)
    .join(", ");

  useEffect(() => {
    scheduled?.refresh?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="border-b border-slate-100 px-8 pb-5 pt-6">
        <h2 className="text-xl font-semibold text-slate-900">Scheduled messages</h2>
        <p className="mt-1 text-sm text-slate-500">
          {summary || "Messages you've scheduled to send later."}
        </p>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {rows.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-6 text-center">
            <ScheduleIcon style={{ fontSize: 40 }} className="text-slate-300" />
            <p className="mt-3 text-sm font-medium text-slate-700">No scheduled messages</p>
            <p className="mt-1 text-sm text-slate-500">
              Use the arrow next to Send to schedule a message for later.
            </p>
          </div>
        ) : (
          rows.map((row) => (
            <ScheduledRow
              key={row.id}
              row={row}
              channelName={names[row.cid] || row.channel_name}
              onOpenChannel={onOpenChannel}
            />
          ))
        )}
      </div>
    </div>
  );
}
